import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Chip, Collapse, Divider, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { generateHexFromString } from '@/_utils/colorUtils';
import { TabBadge } from '@/components/_common';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectGroupedVersionSelections, selectVersionKeys, selectVersionSelections } from '@/redux/selectors/orderPlacementSelectors';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import OPSortedFlatVersions from '../OPSortedFlatVersions';
import OPVersionSelectionsFlat from '../OPVersionSelectionsFlat';
import OPVersionSelectionsList from '../OPVersionSelectionsList';
import Episode from './Episode';
import Feature from './Feature';
import Movie from './Movie';
import PolarisIcon from '../../../_common/PolarisIcon';
import { isObjectAVersion } from './utils';
import { fetchTitleV2 } from '@/redux/slices/titleV2Slice';
import { fetchTitle } from '@/redux/slices/orderPlacementSlice';
import focusConfig from '@/components/Focus/configs';

function extractKeys(obj: VersionGroupedByParent) {
  let keys: number[] = [];
  for (const key in obj) {
    if (key.startsWith('_')) {
      keys.push(parseInt(key.replace('_', '')));
      if (typeof obj[key] === 'object') {
        keys = keys.concat(extractKeys(obj[key]));
      }
    }
  }
  return Array.from(new Set(keys));
}

const OPVersionSelections = () => {
  const isFocus = window.location.pathname.includes('focus');
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const selectedVersions = useAppSelector(selectVersionSelections);
  const groupedByParent = useAppSelector(selectGroupedVersionSelections);
  const versionsByKey = useAppSelector(selectVersionKeys);
  const versionColors = useContext(ConfigContext)?.versionColors || [];
  const focusVersionColor = isFocus && focusConfig.versionColors ? focusConfig.versionColors : undefined;
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const selectTitleById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[titleKey] || {};
  };

  const toggleCollapse = (parentKey: string) => {
    setExpanded(expanded === parentKey ? '' : parentKey);
  };

  // Helper function to check if there's content to display when expanded
  const hasExpandableContent = (children: VersionGroupedByParent) => {
    if (!children || typeof children !== 'object') return false;

    // Check if there are any valid version keys
    const versionKeys = Object.keys(children).filter((key) => key.startsWith('_'));
    if (versionKeys.length === 0) return false;

    // Check if any of the versions have meaningful data
    return versionKeys.some((versionKey) => {
      const version = selectTitleById(versionKey);
      return version && (version.name || version.entity_type);
    });
  };

  const renderVersionTypeChips = (versions: VersionGroupedByParent) => {
    // group the version type and count the number of versions per each
    const versionTypeGroup: Record<string, number> = {};

    // Helper function to process a single version
    const processVersion = (versionId: string) => {
      const version = selectTitleById(versionId);
      if (version?.entity_type === 'version') {
        const versionType: string = version.version_type || version.name;
        if (versionType) {
          versionTypeGroup[versionType] = (versionTypeGroup[versionType] ?? 0) + 1;
        }
      }
    };

    // Helper function to recursively process version structure
    const processVersions = (versionObj: VersionGroupedByParent | string) => {
      if (typeof versionObj === 'string') {
        processVersion(versionObj);
        return;
      }

      // Handle single version object directly
      if (isObjectAVersion(versionObj) && versionObj?.entity_type === 'version') {
        const versionType = versionObj.version_type || versionObj.name;
        if (versionType) {
          versionTypeGroup[versionType] = (versionTypeGroup[versionType] ?? 0) + 1;
        }
        return;
      }

      if (typeof versionObj === 'object' && versionObj !== null) {
        Object.keys(versionObj).forEach((key) => {
          processVersions(versionObj[key]);
        });
      }
    };

    // Process the versions structure
    processVersions(versions);

    return Object.keys(versionTypeGroup)
      .sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()))
      .map((typeKey) => {
        const color =
          focusVersionColor && focusVersionColor[typeKey.toLowerCase()]
            ? focusVersionColor[typeKey.toLowerCase()]
            : versionColors[typeKey.toLowerCase()] || generateHexFromString(typeKey);
        return (
          <Chip
            key={typeKey}
            label={
              <>
                <Typography component="span" sx={{ color: 'background.default', verticalAlign: '-5px' }}>
                  {typeKey.toUpperCase()}
                </Typography>
                <TabBadge count={versionTypeGroup[typeKey]} />
              </>
            }
            icon={<PolarisIcon icon="check" height={15} style={{ color: theme.palette.background.default }} />}
            sx={{
              alignItems: 'center',
              border: '1px solid',
              borderRadius: 5,
              color: 'background.default',
              borderColor: color,
              '&, &.MuiChip-root.MuiChip-filled': {
                backgroundColor: color
              },
              '&, &.MuiChip-root:not(.MuiChip-clickable).MuiChip-outlined': {
                backgroundColor: color
              },
              '& .MuiBadge-root': {
                backgroundColor: 'background.paper',
                color
              }
            }}
          />
        );
      });
  };

  // if (!Object.keys(groupedByParent)[0]) return null;

  // if we don't have a hierarchy, fake it by grouping by series/season/etc
  if (selectedVersions?.length && Object.keys(selectedVersions[0]).includes('piece_id')) return <OPSortedFlatVersions />;

  useEffect(() => {
    // get every key in selectedVersions so we can make sure we have all the keys we need
    const allKeys = extractKeys(groupedByParent);
    const fetchedVersionKeys = Object.keys(versionsByKey);
    // find any keys from allKeys that do not exist in fetchedVersionKeys
    const missingVersionKeys = allKeys.filter((key) => !fetchedVersionKeys.includes(String(key)));
    if (missingVersionKeys.length === 0 || (missingVersionKeys.length === 1 && missingVersionKeys[0] === 0)) return;
    setLoading(true);
    const promises = missingVersionKeys.map((key) => {
      if (isTitleV2) {
        return dispatch(fetchTitleV2(key));
      }

      return dispatch(fetchTitle(key));
    });
    Promise.all(promises);

    // retrieve all the missing versions
  }, [groupedByParent]);

  useEffect(() => {
    if (!loading) return;
    const allKeys = extractKeys(groupedByParent);
    const fetchedVersionKeys = Object.keys(versionsByKey);
    // find any keys from allKeys that do not exist in fetchedVersionKeys
    const missingVersionKeys = allKeys.filter((key) => !fetchedVersionKeys.includes(String(key)));
    if (missingVersionKeys.length === 0 || (missingVersionKeys.length === 1 && missingVersionKeys[0] === 0)) {
      setLoading(false);
      return;
    }
  }, [versionsByKey]);

  return (
    <Box sx={{ pt: 2 }}>
      {loading ? (
        'Loading...'
      ) : (
        <>
          {Object.keys(groupedByParent).map((parentKey) => {
            const parent = selectTitleById(parentKey) || {};
            const parentYear = Number(parent?.releaseYear || parent?.release_year);
            const children = groupedByParent[parentKey];

            if (parent?.entity_type === 'feature' || parent?.entity_type === 'standalone' || parent?.entity_type === 'segment') {
              return <Feature key={parentKey} feature={parent} featureChildren={children} parentYear={parentYear} />;
            }

            if (parent?.entity_type === 'movie' || parent?.entity_type === 'ova') {
              return <Movie key={parentKey} movie={parent} movieChildren={children} parentYear={parentYear} />;
            }

            if (parent?.entity_type === 'episode') {
              return <Episode key={parentKey} episode={parent} episodeChildren={children} parentYear={parentYear} />;
            }

            // Series and/or Season
            return (
              <Card key={parentKey} elevation={0} sx={{ mb: 2 }}>
                <CardHeader
                  sx={{ p: 0, pt: 1, pl: 1.5 }}
                  title={
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      {parent.name}
                      {!!parentYear && (
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          ({parentYear})
                        </Typography>
                      )}
                    </Typography>
                  }
                />
                <CardContent sx={{ pb: 0, pt: 1, pl: 1.5, pr: 1, maxHeight: 500, overflowY: 'scroll' }}>
                  <Collapse in={expanded === parentKey}>
                    <OPVersionSelectionsList selectedVersions={children} />
                  </Collapse>

                  <Collapse in={expanded !== parentKey}>
                    <OPVersionSelectionsFlat selectedVersions={children} />
                  </Collapse>

                  <Divider sx={{ mt: 2, mb: 1 }} />
                </CardContent>
                <CardActions sx={{ pr: 2 }}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    {hasExpandableContent(children) && (
                      <Grid item>
                        <Button variant="text" onClick={() => toggleCollapse(parentKey)}>
                          <PolarisIcon icon="right" height={20} style={{ transform: expanded === parentKey ? 'rotate(-90deg)' : '' }} />
                          {expanded === parentKey ? 'See Less' : 'See More'}
                        </Button>
                      </Grid>
                    )}
                    <Grid item sx={{ flex: hasExpandableContent(children) ? 'none' : 1 }}>
                      <Grid
                        container
                        flexWrap="wrap"
                        columnGap={1}
                        rowGap={1}
                        justifyContent={hasExpandableContent(children) ? 'flex-end' : 'flex-start'}
                      >
                        {expanded !== parentKey && renderVersionTypeChips(children)}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default OPVersionSelections;
